import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import moment from 'moment-timezone';

import { ReactComponent as Alert } from 'assets/icons/alert.svg';
import { ReactComponent as Checkmark } from 'assets/icons/acknowledgement-checkmark.svg';
import { OutlineButton, DarkButton } from '../../Buttons';
import IHaveMoreQuestionsModal from './IHaveMoreQuestionsModal';

const ACKNOWLEDGE_DAILY_INSTRUCTION = gql`
  mutation acknowledgeDailyInstruction($dailyInstructionId: Int!, $acknowledged: Boolean!) {
    acknowledgeDailyInstruction(
      dailyInstructionId: $dailyInstructionId
      acknowledged: $acknowledged
    ) {
      dailyInstruction {
        acknowledged
        id
      }
    }
  }
`;

interface DataInterface {
  nonAcknowledgedIds: Array<number>;
  lastUpdate: string;
}

interface AcknowledgmentInterface {
  dailyInstructionId: number;
  acknowledged?: boolean;
  updatedAt: string;
}
interface Props {
  acknowledgements: Array<AcknowledgmentInterface>;
}

const MedInstructionsAcknowledgment = ({ acknowledgements = [] }: Props) => {
  const [modal, setModal] = useState(false);
  const [nonAcknowledgedIds, setNonAcknowledgedIds] = useState<Array<number>>([]);
  const [lastUpdate, setLastUpdate] = useState<string>('');

  useEffect(() => {
    if (acknowledgements && acknowledgements.length > 0) {
      const { nonAcknowledgedIds, lastUpdate }: DataInterface = acknowledgements.reduce(
        // @ts-ignore
        (acc, { dailyInstructionId, acknowledged, updatedAt }) => {
          const isAcknowledged = acknowledged !== undefined && acknowledged !== null;
          const hasLatestUpdate =
            acc.lastUpdate === '' || moment(updatedAt).isAfter(moment(acc.lastUpdate), 'day');

          const newNonAcknowledgedIds = isAcknowledged
            ? acc.nonAcknowledgedIds
            : [...acc.nonAcknowledgedIds, dailyInstructionId];

          const newLastUpdate = hasLatestUpdate ? updatedAt : acc.lastUpdate;

          return {
            nonAcknowledgedIds: newNonAcknowledgedIds,
            lastUpdate: newLastUpdate
          };
        },
        {
          nonAcknowledgedIds: [],
          lastUpdate: ''
        }
      );

      setNonAcknowledgedIds(nonAcknowledgedIds);
      setLastUpdate(moment(lastUpdate).format('M/D/YY h:mm A zz'));
    }
  }, [acknowledgements]);

  const updateNonAcknowledged = (id: number, acknowledged?: boolean) => {
    if (acknowledged !== undefined && acknowledged !== null) {
      setNonAcknowledgedIds(nonAcknowledgedIds.filter(nonAckId => nonAckId === id));
    }
  };

  const [acknowledgeDailyInstruction] = useMutation(ACKNOWLEDGE_DAILY_INSTRUCTION, {
    onCompleted: ({ acknowledgeDailyInstruction }) => {
      updateNonAcknowledged(
        acknowledgeDailyInstruction.id,
        acknowledgeDailyInstruction.acknowledged
      );
    }
  });

  const handleAcknowledged = (acknowledged: boolean) =>
    nonAcknowledgedIds.forEach(id =>
      acknowledgeDailyInstruction({
        variables: {
          dailyInstructionId: id,
          acknowledged
        }
      })
    );

  if (!acknowledgements || acknowledgements.length === 0) return null;

  const isAcknowledged = nonAcknowledgedIds.length === 0;

  return (
    <>
      <Acknowledgment>
        <AcknowledgmentInstruction>
          <SVGWrapper>{isAcknowledged ? <Checkmark /> : <Alert />}</SVGWrapper>
          <div>
            <strong>Last updated: {lastUpdate}</strong>
            <p>
              {isAcknowledged ? (
                <>Thank you for acknowledging these instructions.</>
              ) : (
                <>
                  Please let us know you&apos;ve seen this recent update by selecting an option
                  here.
                </>
              )}
            </p>
          </div>
        </AcknowledgmentInstruction>
        <ButtonContainer>
          {!isAcknowledged && (
            <OutlineButton
              onClick={() => {
                setModal(true);
                handleAcknowledged(false);
              }}
            >
              I have more questions
            </OutlineButton>
          )}
          {!isAcknowledged && (
            <DarkButton onClick={() => handleAcknowledged(true)}>Got it</DarkButton>
          )}
        </ButtonContainer>
      </Acknowledgment>
      <IHaveMoreQuestionsModal
        visible={modal}
        onDismiss={() => {
          setModal(false);
        }}
      />
    </>
  );
};

export default MedInstructionsAcknowledgment;

const Acknowledgment = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  color: ${({ theme }) => theme.colors.KBNight};
  margin-bottom: 24px;
  background-color: ${({ theme }) => theme.colors.KBBlush};
  padding: 20px 20px 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    padding-left: 16px;
    padding-right: 16px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
    flex-direction: row;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.smallDesktop}) {
    padding-left: 24px;
    padding-right: 24px;
  }
`;

const ButtonContainer = styled.div`
  padding: 0 15px 15px 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;

  & > a,
  & > button {
    margin-top: 20px;
  }

  a,
  button {
    width: 100%;
    white-space: nowrap;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largePhonePortrait}) {
    margin-left: 0;
    flex-direction: row;
    justify-content: space-between;

    a,
    button {
      width: auto;
    }

    a:not(:last-child),
    button:not(:last-child) {
      margin-right: 24px;
    }
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
    padding-right: 0;

    margin-left: 24px;
  }
`;

const SVGWrapper = styled.div`
  width: 28px;
  margin-right: 16px;
`;

const AcknowledgmentInstruction = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
`;
